// Type definitions for Google Analytics
declare global {
    interface Window {
        gtag?: (
            command: "config" | "set" | "event" | "consent",
            targetId: string,
            config?: Record<string, unknown>
        ) => void;
        dataLayer?: unknown[];
    }
}

interface AnalyticsEvent {
    name: string;
    properties?: Record<string, unknown>;
    timestamp?: number;
}

interface AnalyticsConfig {
    enabled?: boolean;
    debug?: boolean;
    providers?: ("gtag" | "mixpanel" | "amplitude")[];
}

type AnalyticsProvider = "gtag" | "mixpanel" | "amplitude" | "console";

class Analytics {
    private events: AnalyticsEvent[] = [];
    private isEnabled: boolean;
    private debug: boolean;
    private providers: AnalyticsProvider[];
    private maxEventHistory = 1000;

    constructor(config?: AnalyticsConfig) {
        this.isEnabled =
            config?.enabled ??
            (import.meta.env.PROD && import.meta.env.VITE_ANALYTICS_ENABLED === 'true');

        this.debug = config?.debug ?? import.meta.env.DEV;

        this.providers = config?.providers ?? ["gtag"];

        // Add console provider in development
        if (this.debug && !this.providers.includes("console")) {
            this.providers.push("console");
        }
    }

    track(name: string, properties?: object): void {
        if (!this.isEnabled && !this.debug) return;

        const event: AnalyticsEvent = {
            name,
            properties: this.sanitizeProperties(properties),
            timestamp: Date.now(),
        };

        // Store event in history (with size limit)
        this.events.push(event);
        if (this.events.length > this.maxEventHistory) {
            this.events.shift();
        }

        // Send to analytics providers
        this.sendEvent(event);
    }

    private sanitizeProperties(
        properties?: object
    ): Record<string, unknown> | undefined {
        if (!properties) return undefined;

        // Remove sensitive data and ensure serializable
        const sanitized: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(properties)) {
            // Skip sensitive keys
            if (this.isSensitiveKey(key)) continue;

            // Ensure value is serializable
            try {
                JSON.stringify(value);
                sanitized[key] = value;
            } catch {
                sanitized[key] = String(value);
            }
        }

        return sanitized;
    }

    private isSensitiveKey(key: string): boolean {
        const sensitiveKeys = [
            "password",
            "token",
            "secret",
            "key",
            "auth",
            "email",
            "phone",
            "ssn",
            "credit_card",
        ];

        return sensitiveKeys.some((sensitive) =>
            key.toLowerCase().includes(sensitive)
        );
    }

    private sendEvent(event: AnalyticsEvent): void {
        this.providers.forEach((provider) => {
            try {
                switch (provider) {
                    case "gtag":
                        this.sendToGtag(event);
                        break;
                    case "mixpanel":
                        this.sendToMixpanel(event);
                        break;
                    case "amplitude":
                        this.sendToAmplitude(event);
                        break;
                    case "console":
                        this.sendToConsole(event);
                        break;
                }
            } catch (error) {
                console.warn(`Failed to send event to ${provider}:`, error);
            }
        });
    }

    private sendToGtag(event: AnalyticsEvent): void {
        if (typeof window !== "undefined" && window.gtag) {
            window.gtag("event", event.name, {
                ...event.properties,
                timestamp: event.timestamp,
            });
        } else if (this.debug) {
            console.warn("gtag is not available");
        }
    }

    private sendToMixpanel(event: AnalyticsEvent): void {
        const win = window as unknown as Record<string, unknown>;
        if (typeof window !== "undefined" && win.mixpanel) {
            const mixpanel = win.mixpanel as { track: (name: string, props?: Record<string, unknown>) => void };
            mixpanel.track(event.name, event.properties);
        } else if (this.debug) {
            console.warn("Mixpanel is not available");
        }
    }

    private sendToAmplitude(event: AnalyticsEvent): void {
        const win = window as unknown as Record<string, unknown>;
        if (typeof window !== "undefined" && win.amplitude) {
            const amplitude = win.amplitude as { getInstance: () => { logEvent: (name: string, props?: Record<string, unknown>) => void } };
            amplitude
                .getInstance()
                .logEvent(event.name, event.properties);
        } else if (this.debug) {
            console.warn("Amplitude is not available");
        }
    }

    private sendToConsole(event: AnalyticsEvent): void {
        console.group(`📊 Analytics Event: ${event.name}`);
        console.log("Properties:", event.properties);
        console.log("Timestamp:", event.timestamp ? new Date(event.timestamp).toISOString() : "N/A");
        console.groupEnd();
    }

    trackError(error: Error, context?: Record<string, unknown>): void {
        this.track("error_occurred", {
            error_message: error.message,
            error_stack: error.stack?.substring(0, 1000), // Limit stack trace length
            error_name: error.name,
            user_agent: navigator.userAgent,
            url: window.location.href,
            timestamp: Date.now(),
            ...context,
        });
    }

    trackPerformance(metric: string, value: number, unit = "ms"): void {
        this.track("performance_metric", {
            metric,
            value,
            unit,
            url: window.location.pathname,
            user_agent: navigator.userAgent,
        });
    }

    // User interaction tracking
    trackUserAction(
        action: string,
        target: string,
        properties?: Record<string, unknown>
    ): void {
        this.track("user_action", {
            action,
            target,
            page: window.location.pathname,
            timestamp: Date.now(),
            ...properties,
        });
    }

    // Page view tracking
    trackPageView(path?: string): void {
        this.track("page_view", {
            page: path || window.location.pathname,
            referrer: document.referrer,
            title: document.title,
            url: window.location.href,
        });
    }

    // Conversion tracking
    trackConversion(event: string, value?: number, currency?: string): void {
        this.track("conversion", {
            event,
            value,
            currency: currency || "USD",
            page: window.location.pathname,
        });
    }

    // Session tracking
    trackSession(action: "start" | "end"): void {
        this.track(`session_${action}`, {
            timestamp: Date.now(),
            page: window.location.pathname,
            user_agent: navigator.userAgent,
        });
    }

    // Feature usage tracking
    trackFeatureUsage(feature: string, properties?: Record<string, unknown>): void {
        this.track("feature_used", {
            feature,
            page: window.location.pathname,
            ...properties,
        });
    }

    // Get analytics data (for debugging)
    getEventHistory(): AnalyticsEvent[] {
        return [...this.events];
    }

    // Clear event history
    clearEventHistory(): void {
        this.events = [];
    }

    // Enable/disable analytics
    setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
    }

    // Check if analytics is enabled
    isAnalyticsEnabled(): boolean {
        return this.isEnabled;
    }

    // Set debug mode
    setDebug(debug: boolean): void {
        this.debug = debug;
        if (debug && !this.providers.includes("console")) {
            this.providers.push("console");
        } else if (!debug && this.providers.includes("console")) {
            this.providers = this.providers.filter((p) => p !== "console");
        }
    }
}

// Create analytics instance
export const analytics = new Analytics();

// Convenience functions for common tracking patterns
export const trackClick = (
    elementId: string,
    properties?: Record<string, unknown>
) => {
    analytics.trackUserAction("click", elementId, properties);
};

export const trackFormSubmit = (
    formId: string,
    properties?: Record<string, unknown>
) => {
    analytics.trackUserAction("form_submit", formId, properties);
};

export const trackAPICall = (
    endpoint: string,
    method: string,
    duration?: number
) => {
    analytics.track("api_call", {
        endpoint,
        method,
        duration,
        timestamp: Date.now(),
    });
};

export const trackError = (error: Error, context?: Record<string, unknown>) => {
    analytics.trackError(error, context);
};

// React hook for analytics
export const useAnalytics = () => {
    return {
        track: analytics.track.bind(analytics),
        trackError: analytics.trackError.bind(analytics),
        trackUserAction: analytics.trackUserAction.bind(analytics),
        trackPageView: analytics.trackPageView.bind(analytics),
        trackFeatureUsage: analytics.trackFeatureUsage.bind(analytics),
        isEnabled: analytics.isAnalyticsEnabled(),
    };
};

// Default export
export default analytics;
